// react and js
import React from "react";
import PropTypes from "prop-types";
import { FastField } from "formik";

// mui

// theme
import { ModalContentTextField } from "../theme/styled";

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
export function SmartTextInput({ textFieldName, defaultLimits, formik }) {
  const handleChange = (event) => {
    const currentValue = event.target.value;
    const limits = defaultLimits[textFieldName];

    // Mark field as touched so errors will be displayed immediately
    formik.setFieldTouched(textFieldName, true);
    
    // Reset error
    formik.setFieldError(textFieldName, undefined);

    // Check max length
    if (limits.maxLength && currentValue.length > limits.maxLength) {
      formik.setFieldError(textFieldName, `Text too long. Maximum ${limits.maxLength} characters allowed.`);
    }

    // Check for disallowed characters
    if (limits.disallowedPattern && limits.disallowedPattern.test(currentValue)) {
      const invalidChars = currentValue.match(new RegExp(limits.disallowedPattern, "g"));
      const uniqueInvalidChars = [...new Set(invalidChars)];
      formik.setFieldError(textFieldName, `Invalid characters detected: ${uniqueInvalidChars.join(", ")}`);
    }

    // Check for empty values
    if (limits && limits.emptyAllowed === false && currentValue === "") {
      formik.setFieldError(textFieldName, `Field cannot be empty.`);
    }

    // Set field
    formik.setFieldValue(textFieldName, currentValue);

    // Mark field as touched
    formik.setFieldTouched(textFieldName, true);
  };

  const handleBlur = (event) => {
    const currentValue = event.target.value;

    // Set field
    formik.setFieldValue(textFieldName, currentValue);

    // Mark field as touched
    formik.setFieldTouched(textFieldName, true);
  };

  return (
    <FastField name={textFieldName}>
      {({ field, meta }) => (
        <ModalContentTextField
          {...field}
          onChange={handleChange}
          onBlur={handleBlur}
          fullWidth
          size="small"
          error={Boolean(meta.touched && meta.error)}
          InputLabelProps={{ shrink: true }}
          {...formik.textFieldProps}
        />
      )}
    </FastField>
  );
}

SmartTextInput.propTypes = {
  textFieldName: PropTypes.string.isRequired,
  defaultLimits: PropTypes.objectOf(
    PropTypes.shape({
      maxLength: PropTypes.number,
      disallowedPattern: PropTypes.instanceOf(RegExp),
      emptyAllowed: PropTypes.bool,
    })
  ).isRequired,
  formik: PropTypes.shape({
    setFieldTouched: PropTypes.func.isRequired,
    setFieldError: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    textFieldProps: PropTypes.object,
  }).isRequired,
};


// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
export function SmartNumberInput({ textFieldName, defaultLimits, formik }) {
  const handleChange = (event) => {
    const {value} = event.target;
    const numValue = parseFloat(value);
    const limits = defaultLimits[textFieldName];

    // Mark field as touched so errors will be displayed immediately
    formik.setFieldTouched(textFieldName, true);

    // Check if field is empty first
    if (value === '' || value.trim() === '') {
      formik.setFieldError(textFieldName, 'Value cannot be empty');
    } 
    // Then validate against limits if value is not empty
    else if (limits?.min !== undefined && numValue < limits.min) {
      formik.setFieldError(textFieldName, `Value cannot be below ${limits.min}`);
    } else if (limits?.max !== undefined && numValue > limits.max) {
      formik.setFieldError(textFieldName, `Value cannot be above ${limits.max}`);
    } else {
      formik.setFieldError(textFieldName, undefined);
    }

    // Set the raw value as the user types (no formatting during typing)
    formik.setFieldValue(textFieldName, value);
  };

  const handleBlur = (event) => {
    const {value} = event.target;
    const limits = defaultLimits[textFieldName];

    // Check for empty value on blur as well
    if (value === '' || value.trim() === '') {
      formik.setFieldError(textFieldName, 'Value cannot be empty');
      formik.setFieldTouched(textFieldName, true);
      return;
    }

    // Only format when user finishes editing (loses focus) and value is not empty
    if (!Number.isNaN(parseFloat(value))) {
      const digits = limits?.digits;
      const formattedValue = parseFloat(value).toFixed(digits);
      formik.setFieldValue(textFieldName, formattedValue);
    }

    // Mark field as touched on blur as well
    formik.setFieldTouched(textFieldName, true);
  };

  return (
    <ModalContentTextField
      {...formik.getFieldProps(textFieldName)}
      onChange={handleChange}
      onBlur={handleBlur}
      fullWidth
      size="small"
      type="number"
      inputProps={{
        step: defaultLimits[textFieldName]?.step,
        // min: defaultLimits[textFieldName]?.min !== undefined ? Math.floor(defaultLimits[textFieldName].min) : undefined,
        // max: defaultLimits[textFieldName]?.max,
      }}
      error={Boolean(formik.touched[textFieldName] && formik.errors[textFieldName])}
      InputLabelProps={{ shrink: true }}
    />
  );
}

SmartNumberInput.propTypes = {
  textFieldName: PropTypes.string.isRequired,
  defaultLimits: PropTypes.objectOf(
    PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number,
      digits: PropTypes.number,
      step: PropTypes.number,
    })
  ).isRequired,
  formik: PropTypes.shape({
    setFieldTouched: PropTypes.func.isRequired,
    setFieldError: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    getFieldProps: PropTypes.func.isRequired,
    touched: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
  }).isRequired,
};